import { DEFAULT_PLACEHOLDER } from '@constants/generic';
import {
  BASIC_INFORMATION,
  CUSTOM_FIELDS,
  MEMBER_FIELD_CATALOG,
  MEMBER_FIELDS
} from '@features/forms/components/wizard-steps/fields-step/constants';
import { FormSubmission } from '@features/forms/types';
import { FormSubmissionDetailApiResponse } from '@features/forms/types/formSubmissionDetailApi';
import {
  SubmissionDrawerData,
  SubmissionFormFieldSection,
  SubmissionFormFieldValue
} from '@features/forms/types/submissionDrawer';
import { formatISODate } from '@utils/date';

import { formatSubmissionVersion } from './formatSubmissionDisplay';
import { normalizeSubmissionFieldLabel } from './mapFormApiFields';
import { mapAddToMode, mapSubmissionPaymentStatus } from './mapFormSubmission';
import { mapSubmissionTaxDetails } from './mapSubmissionTaxDetails';
import { parseSubmissionPaymentAmount } from './parseSubmissionPaymentAmount';

const SECTION_TITLES = {
  basic: BASIC_INFORMATION.title.toUpperCase(),
  member: MEMBER_FIELDS.title.toUpperCase(),
  other: CUSTOM_FIELDS.title.toUpperCase()
} as const;

const SECTION_ORDER = ['basic', 'member', 'other'] as const;

const BASIC_FIELD_NAMES = new Set([
  'registration_name',
  'mobile_number',
  'primary_number'
]);

const MEMBER_FIELD_LABELS = new Set(
  MEMBER_FIELD_CATALOG.map((entry) =>
    entry.label.toLowerCase().replace(/\s+/g, '_')
  )
);

const inferSubmissionFieldCategory = (
  fieldName: string,
  fieldType: string
): keyof typeof SECTION_TITLES => {
  const normalizedType = fieldType.toLowerCase().replace(/-/g, '_');
  if (normalizedType === 'basic') return 'basic';
  if (normalizedType === 'member') return 'member';
  if (normalizedType === 'custom') return 'other';

  const normalizedName = fieldName
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_');

  if (BASIC_FIELD_NAMES.has(normalizedName)) return 'basic';
  if (
    normalizedType === 'mobile_number' &&
    !normalizedName.includes('alternate')
  ) {
    return 'basic';
  }

  if (MEMBER_FIELD_LABELS.has(normalizedName)) return 'member';

  return 'other';
};

const toFieldValue = (
  values: string[],
  fieldType: string
): SubmissionFormFieldValue => {
  if (!values.length) {
    return DEFAULT_PLACEHOLDER;
  }

  const normalizedType = fieldType.toLowerCase().replace(/-/g, '_');
  if (normalizedType === 'file') {
    return values[0] ?? DEFAULT_PLACEHOLDER;
  }

  if (values.length === 1) {
    return values[0];
  }
  return values;
};

export const mapFormFieldSections = (
  fields: FormSubmissionDetailApiResponse['form_submission']
): SubmissionFormFieldSection[] => {
  const grouped: Record<
    keyof typeof SECTION_TITLES,
    SubmissionFormFieldSection
  > = {
    basic: { title: SECTION_TITLES.basic, fields: [] },
    member: { title: SECTION_TITLES.member, fields: [] },
    other: { title: SECTION_TITLES.other, fields: [] }
  };

  fields.forEach((field) => {
    const category = inferSubmissionFieldCategory(
      field.field_name,
      field.field_type
    );
    grouped[category].fields.push({
      label: normalizeSubmissionFieldLabel(field.field_name, field.field_type),
      value: toFieldValue(field.field_value ?? [], field.field_type),
      fieldType: field.field_type
    });
  });

  return SECTION_ORDER.filter((key) => grouped[key].fields.length > 0).map(
    (key) => grouped[key]
  );
};

const formatTransactionDateTime = (
  date?: string,
  usesDateOnly?: boolean
): string => {
  if (!date) {
    return DEFAULT_PLACEHOLDER;
  }
  const formatted = formatISODate({
    date,
    format: usesDateOnly ? 'DD MMM YYYY' : 'DD MMM YYYY at hh:mm am/pm'
  });
  return formatted ? `${formatted}` : DEFAULT_PLACEHOLDER;
};

const isManualPayment = (paymentMode?: string): boolean =>
  !!paymentMode && paymentMode !== 'automatic' && paymentMode !== 'online';

const buildPaymentStatusLabel = (
  payment: FormSubmissionDetailApiResponse['payment_details']
): string => {
  if (payment.payment_method) {
    return `Paid via ${payment.payment_method}`;
  }
  if (
    payment.payment_mode === 'automatic' ||
    payment.payment_mode === 'online'
  ) {
    return 'Paid online via CoFee';
  }
  if (payment.payment_mode) {
    return `Paid ${payment.payment_mode}`;
  }
  return 'Paid online via CoFee';
};

export const mapSubmissionDetailToListItem = (
  detail: FormSubmissionDetailApiResponse
): FormSubmission => {
  const addToMode = mapAddToMode(detail.add_to_mode);
  const payment = detail.payment_details;

  return {
    id: detail.id,
    name: detail.name,
    phone: detail.mobile,
    amountPaid: parseSubmissionPaymentAmount(payment.paid_amount),
    totalAmount: parseSubmissionPaymentAmount(payment.total_amount),
    addedTo: {
      mode: addToMode,
      groupId: detail.group?.id,
      groupName: detail.group?.name
    },
    source: detail.source,
    paidAt: payment.paid_at ?? '',
    formVersion: detail.version,
    paymentId: payment.payment_order_id ?? '',
    paymentStatus: mapSubmissionPaymentStatus(payment.settlement_status)
  };
};

export const mapSubmissionDetailToDrawer = (
  detail: FormSubmissionDetailApiResponse
): SubmissionDrawerData => {
  const submission = mapSubmissionDetailToListItem(detail);
  const payment = detail.payment_details;
  const discountAmount = parseSubmissionPaymentAmount(
    payment.total_discount_amount
  );
  const subtotal =
    parseSubmissionPaymentAmount(payment.fee_amount) ||
    parseSubmissionPaymentAmount(payment.total_amount) ||
    submission.totalAmount;
  const taxDetails = mapSubmissionTaxDetails(payment.tax_details);

  const payments: SubmissionDrawerData['payments'] = [
    {
      id: 'registration-fee',
      title: 'Registration fee',
      amount:
        parseSubmissionPaymentAmount(payment.paid_amount) ||
        submission.amountPaid,
      statusLabel: buildPaymentStatusLabel(payment),
      statusVariant:
        submission.paymentStatus === 'processing' ? 'processing' : 'paid',
      transaction: payment.payment_order_id
        ? {
            transactionId: payment.payment_order_id,
            paidOn: isManualPayment(payment.payment_mode)
              ? formatTransactionDateTime(payment.paid_at, true)
              : formatTransactionDateTime(payment.paid_at),
            ...(isManualPayment(payment.payment_mode) && payment.marked_paid_at
              ? {
                  markedPaidOn: formatTransactionDateTime(
                    payment.marked_paid_at
                  )
                }
              : {}),
            ...(payment.settled_at
              ? { settledOn: formatTransactionDateTime(payment.settled_at) }
              : {}),
            ...(payment.settlement_utr
              ? { settlementUtr: payment.settlement_utr }
              : {}),
            notes: payment.note
          }
        : undefined
    }
  ];

  if (discountAmount > 0) {
    payments.push({
      id: 'total-discount',
      title: 'Total discount',
      amount: discountAmount,
      statusLabel: 'Discount',
      statusVariant: 'discount'
    });
  }

  return {
    submission,
    memberId: detail.member_id,
    isPartialPaymentEnabled: Boolean(payment.is_partial_payment_enabled),
    formName: detail.form_name,
    organizationName: detail.organisation_name,
    branchName: detail.branch_name,
    generalInfo: [
      { label: 'Form name', value: detail.form_name },
      {
        label: 'Submitted on',
        value: formatSubmissionVersion(detail.version)
      },
      { label: 'Organization', value: detail.organisation_name },
      { label: 'Branch', value: detail.branch_name },
      {
        label: 'Added to',
        value:
          submission.addedTo.mode === 'member_list'
            ? 'Member list'
            : submission.addedTo.groupName ?? DEFAULT_PLACEHOLDER
      }
    ],
    feeSummary: {
      subtotal,
      discountAmount: discountAmount > 0 ? discountAmount : undefined,
      totalAmount:
        parseSubmissionPaymentAmount(payment.total_amount) ||
        submission.totalAmount,
      taxDetails
    },
    payments,
    formFieldSections: mapFormFieldSections(detail.form_submission ?? [])
  };
};

export default mapSubmissionDetailToDrawer;
